import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { NavController } from '@ionic/angular';
import { Store, select } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { User } from '../../providers/user/user.service';
import { getUser } from '../../providers/user/store/user.selectors';
import { ChatService } from '../../providers/chat/chat.service';
import { AuthService } from '../../providers/auth/auth.service';
import { ViewportService } from '../../providers/viewport/viewport.service';

@Component({
	selector: 'app-nav-bar',
	templateUrl: './app-nav-bar.component.html',
	styleUrls: ['./app-nav-bar.component.scss'],
})
export class AppNavBarComponent implements OnInit, OnDestroy {
	/** When true, renders as standalone bar (not inside ion-tabs) */
	@Input() standalone = false;
	
	/** Emits when user clicks a tab that requires auth but isn't logged in */
	@Output() authRequired = new EventEmitter<{ event?: Event; redirectUrl?: string }>();
	
	/** Emits when user clicks add listing */
	@Output() addListingClick = new EventEmitter<Event>();

	user: User | null = null;
	unreadCount = 0;
	activeTab = 'home';

	private userSubscription: Subscription;
	private unreadSubscription: Subscription;
	private routerSubscription: Subscription;

	constructor(
		private router: Router,
		private navCtrl: NavController,
		private store: Store,
		private chatService: ChatService,
		private authService: AuthService,
		private viewport: ViewportService
	) {}

	ngOnInit(): void {
		this.userSubscription = this.store.pipe(select(getUser)).subscribe((user: User) => {
			this.user = user;
		});

		this.unreadSubscription = this.chatService.unreadCount$.subscribe((count) => {
			this.unreadCount = count;
		});

		this.routerSubscription = this.router.events
			.pipe(filter(event => event instanceof NavigationEnd))
			.subscribe((event: NavigationEnd) => {
				this.updateActiveTab(event.url);
			});

		// Set initial active tab
		this.updateActiveTab(this.router.url);
	}

	ngOnDestroy(): void {
		this.userSubscription?.unsubscribe();
		this.unreadSubscription?.unsubscribe();
		this.routerSubscription?.unsubscribe();
	}

	private updateActiveTab(url: string): void {
		if (url === '/' || url === '/home' || url.startsWith('/home/') || url.startsWith('/horse/') || url.startsWith('/tack/')) {
			this.activeTab = 'home';
		} else if (url.startsWith('/chat')) {
			this.activeTab = 'chat';
		} else if (url.startsWith('/account')) {
			this.activeTab = 'account';
		} else if (url.startsWith('/settings')) {
			this.activeTab = 'settings';
		}
	}

	private isAuthenticated(): boolean {
		return !!this.authService.token?.access;
	}

	navigateTo(tab: string, ev?: Event): void {
		// Check auth for protected routes
		if (!this.isAuthenticated() && ['chat', 'account', 'settings'].includes(tab)) {
			const routes: Record<string, string> = {
				chat: '/chat',
				account: '/account',
				settings: '/settings'
			};
			const redirectUrl = routes[tab];
			if (redirectUrl) {
				// Show tab as active even when blocked by login
				this.activeTab = tab;
				this.authRequired.emit({ event: ev, redirectUrl });
			}
			return;
		}

		const routes: Record<string, string> = {
			home: '/home',
			chat: '/chat',
			account: '/account',
			settings: '/settings'
		};

		const route = routes[tab];
		if (route) {
			this.navCtrl.navigateRoot(route);
		}
	}

	onAddListing(ev: Event): void {
		this.addListingClick.emit(ev);
	}

	isDesktopViewport(): boolean {
		return this.viewport.isDesktop;
	}
}

